// Gráfico da curva de desembolso Ampli DENTRO da tela de Desembolso (ponto 3 do pedido
// de stg): mesma série que a Projeção Financeira já publica (`resolveAmpliSeries`,
// `getDetail`/`getFollowUps`), lida do próprio `activeProject` — nenhuma rota nova. O
// vínculo com a Projeção Financeira é preservado à parte, em
// `AmpliDisbursementCoverageCard` ("Abrir o módulo Desembolso"), que continua
// existindo e apontando para esta tela.
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { formatNumberToCurrency } from "@utils/formatNumber";

import { resolveAmpliSeries } from "../financialProjection/ampliSeries";
import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PLAIN_UNAVAILABLE_HINT =
  "Esta obra ainda não tem previsto canônico para gerar a curva.";

const STATE_DETAILS = Object.freeze({
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  unavailable: PLAIN_UNAVAILABLE_HINT,
});

/** `"MM/YYYY"` → chave ordenável `YYYYMM`, sem depender do locale do runtime. */
const sortKey = (date) => {
  const [month, year] = String(date ?? "").split("/");
  return `${year ?? "0"}${(month ?? "0").padStart(2, "0")}`;
};

const AmpliCurveChart = ({ project }) => {
  const series = useMemo(
    () => [...resolveAmpliSeries(project)].sort(
      (left, right) => (sortKey(left.date) < sortKey(right.date) ? -1 : 1),
    ),
    [project],
  );

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_DETAILS[curve.status] ?? null;
  const hasSeries = series.length > 0;

  const data = {
    labels: series.map((point) => point.date),
    datasets: [
      {
        label: "Desembolso Ampli (R$)",
        data: series.map((point) => Number(point.value) || 0),
        backgroundColor: "#2563EB",
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => formatNumberToCurrency(context.parsed.y, false),
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value) => formatNumberToCurrency(value, false) },
      },
    },
  };

  return (
    <section className={`ampli-curve-chart ampli-curve-chart--${curve.status}`}>
      <h2>Desembolso Ampli</h2>
      <p className="card-label">{ampliCurveStateLabel(curve.status)}</p>
      {detail ? <p>{detail}</p> : null}
      {hasSeries ? (
        <Bar data={data} options={options} aria-label="Gráfico do desembolso mensal da obra" />
      ) : (
        <p className="empty-state">Nenhuma curva de desembolso para desenhar.</p>
      )}
    </section>
  );
};

export default AmpliCurveChart;
